import React, { useContext, useEffect, useRef } from 'react';
import { Button, Tab, Tabs } from 'react-bootstrap';
import HttpRequestInput from '../HttpRequestInput/HttpRequestInput';
import { SandBoxContext } from '../SandboxModal';
import _ from 'lodash';
import KeyValueTable from './KeyValueTable';
import RequestBody from './RequestBody';
import { useIsMount } from '../../../hooks/useIsMountHook';
import acApi from '../../../axios/ac/acApi';
import { encodeBase64, generateUniqueId } from '../../../utils/stringUtils';
import BzmIcon from '../../icons/BzmIcon';
import { confirm } from '../../ui/modal/BlazeConfirmationModal';
import { HttpKeyValue, Matcher, MatcherDsl, RequestDsl, MsgRequestDsl } from '../../../types/rest/dslTypes';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import { useDispatch } from 'react-redux';
import { getAccountAiConsent } from '../../../utils/bzmUserConfigUtil';
import clsx from 'clsx';
import styles from './SandBoxRequestDefinitionClient.module.css';

const CONTAINS_TEMPLATE = /\$\{[^}]+}/;

/** Matcher types that require AI to resolve (param-level). */
const AI_PARAM_MATCHERS = new Set<Matcher>([Matcher.matches, Matcher.not_matches]);

/** Matcher types that require AI to resolve (body-level). */
const AI_BODY_MATCHERS = new Set<Matcher>([
  Matcher.matches,
  Matcher.not_matches,
  Matcher.matches_json,
  Matcher.matches_xml,
  Matcher.matches_xml_cdata,
]);

/** Returns true if the DSL contains any matcher that needs AI to generate a satisfying value. */
function hasAiRequiredMatchers(dsl: any): boolean {
  const req = dsl?.requestDsl as (RequestDsl & MsgRequestDsl) | undefined;
  if (!req) return false;

  // URL matcher: matches_url without a BlazeData template
  const urlMatcher = (req as RequestDsl).url;
  if (
    urlMatcher?.matcherName === Matcher.matches_url &&
    urlMatcher.matchingValue &&
    !CONTAINS_TEMPLATE.test(urlMatcher.matchingValue)
  ) {
    return true;
  }

  // Header / queryParam / cookie matchers
  for (const list of [req.headers, (req as RequestDsl).queryParams, (req as RequestDsl).cookies]) {
    for (const m of list ?? []) {
      if (AI_PARAM_MATCHERS.has(m.matcherName) && m.matchingValue && !CONTAINS_TEMPLATE.test(m.matchingValue)) {
        return true;
      }
    }
  }

  // Body matchers
  for (const m of req.body ?? []) {
    if (AI_BODY_MATCHERS.has(m.matcherName) && m.matchingValue && !CONTAINS_TEMPLATE.test(m.matchingValue)) {
      return true;
    }
  }

  return false;
}

const SandBoxRequestDefinitionClient = () => {
  const { state, setState } = useContext(SandBoxContext);
  const { httpRequest } = state.sandboxTransaction;

  const updateHandler = (field: string | number, value: any) => {
    setState(prevState => {
      const update = _.cloneDeep(prevState.sandboxTransaction.httpRequest);
      update[field] = value;
      return {
        ...prevState,
        sandboxTransaction: {
          ...prevState.sandboxTransaction,
          httpRequest: update,
        },
      };
    });
  };

  const isMount = useIsMount();
  const isMountedRef = useRef(true);
  const patchAbortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      isMountedRef.current = false;
      patchAbortRef.current?.abort();
    },
    [],
  );
  const dispatch = useDispatch();

  const updateSandbox = async () => {
    if (!isMount) {
      patchAbortRef.current?.abort();
      const controller = new AbortController();
      patchAbortRef.current = controller;
      try {
        await acApi.sandbox.updateSandbox(
          { httpRequest: state.sandboxTransaction.httpRequest },
          { signal: controller.signal },
        );
      } catch (e) {
        if (e?.name !== 'AbortError' && e?.name !== 'CanceledError') {
          dispatch(showToastNotification(ToastType.Danger, 'Cannot Update Sandbox'));
        }
      }
    }
  };

  const isAutofilling = !!state.isAutofilling;

  /** Computes the base httpRequest values from the literal DSL matchers (client-side). */
  function computeLiteralValues() {
    const sandboxDefinitionRequest = _.cloneDeep(state.sandboxTransaction.dsl.requestDsl);
    const definitionHeaders = sandboxDefinitionRequest.headers.map(header => ({
      id: generateUniqueId([]),
      name: header.key,
      value: header.matchingValue,
    }));
    const definitionParams = sandboxDefinitionRequest.queryParams.map(header => ({
      id: generateUniqueId([]),
      name: header.key,
      value: header.matchingValue,
    }));

    const getBodyByMatcher = (bodyArray: any[], matcherName: Matcher) => {
      return bodyArray && bodyArray.length > 0
        ? bodyArray.filter(body => body.matcherName === matcherName)[0]?.matchingValue
        : undefined;
    };
    const getSampleBodyByMatcher = (bodyArray: any[], matcherName: Matcher) => {
      return bodyArray && bodyArray.length > 0
        ? bodyArray.filter(body => body.matcherName === matcherName)[0]?.sampleBody
        : undefined;
    };
    // Legacy fallback: only relevant for a freshly-loaded, never-yet-edited sandbox
    // transaction whose matcher rows don't carry their own sampleBody yet. Guarded
    // against malformed/non-JSON content so it degrades to `undefined` instead of throwing.
    const resolveLegacySampleBody = (key: 'json' | 'xpath'): string | undefined => {
      if (!state.sandboxTransaction.sampleBody) {
        return undefined;
      }
      try {
        if (typeof state.sandboxTransaction.sampleBody !== 'string') {
          const value = _.cloneDeep(state.sandboxTransaction.sampleBody[key]);
          return value ? Buffer.from(value).toString('base64') : undefined;
        }
        const parsed = JSON.parse(state.sandboxTransaction.sampleBody as string);
        return parsed?.[key] ? Buffer.from(parsed[key]).toString('base64') : undefined;
      } catch {
        return undefined;
      }
    };

    let body = getBodyByMatcher(sandboxDefinitionRequest.body, Matcher.equals);
    if (body === undefined) {
      body = getBodyByMatcher(sandboxDefinitionRequest.body, Matcher.matches_json);
      if (body !== undefined) {
        body =
          getSampleBodyByMatcher(sandboxDefinitionRequest.body, Matcher.matches_json) ??
          resolveLegacySampleBody('json');
      }
    }
    if (body === undefined) {
      body = getBodyByMatcher(sandboxDefinitionRequest.body, Matcher.matches_xml);
      if (body !== undefined) {
        body =
          getSampleBodyByMatcher(sandboxDefinitionRequest.body, Matcher.matches_xml) ??
          resolveLegacySampleBody('xpath');
      }
    }

    return {
      method: sandboxDefinitionRequest.method,
      path: sandboxDefinitionRequest.host,
      headers: definitionHeaders,
      queryParameters: definitionParams,
      body,
    };
  }

  /** Shows the "lose data" confirmation when the form already has values. Returns true to proceed. */
  const confirmAutofill = (): Promise<boolean> => {
    const isEmpty =
      !httpRequest.body &&
      _.size(httpRequest.queryParameters) === 0 &&
      _.size(httpRequest.headers) === 0 &&
      !httpRequest.path &&
      httpRequest.method === state.sandboxTransaction.dsl.requestDsl.method;

    if (isEmpty) {
      return Promise.resolve(true);
    }

    const aiSkipped = hasAiRequiredMatchers(state.sandboxTransaction.dsl) && !getAccountAiConsent();

    const modalBody = (
      <div>
        <p>
          <span className="text-break"> You might lose existing testing data. </span>
        </p>
        {aiSkipped && (
          <span className="text-break">
            {' '}
            Regex patterns will not be replaced because AI consent has not been accepted.{' '}
          </span>
        )}
      </div>
    );
    return confirm({
      confirmation: modalBody,
      title: 'Prepare data from current definition?',
      yesOptionVariant: 'primary',
      yesOptionTitle: 'Continue',
    })
      .then(() => true)
      .catch(() => false);
  };

  const autofillValues = async () => {
    const proceed = await confirmAutofill();
    if (!proceed) return;

    setState(prev => ({ ...prev, isAutofilling: true }));
    try {
      // Compute literal values from DSL matchers (method, path, plain headers/params/body)
      const literalValues = computeLiteralValues();

      // If BlazeData generation is still running AND the DSL actually uses ${templates},
      // wait for generation to finish before calling the backend.
      // If no templates exist in the DSL there is nothing to resolve, so no need to wait.
      const dslHasTemplates = CONTAINS_TEMPLATE.test(JSON.stringify(state.sandboxTransaction.dsl));
      if (dslHasTemplates && state.sandboxTransaction.blazeDataGenerationCompleted === false) {
        const deadline = Date.now() + 90_000;
        while (Date.now() < deadline && isMountedRef.current) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const ready = (await acApi.sandbox.getGenerationStatus())?.data?.result;
            if (ready === true) {
              setState(prev => ({
                ...prev,
                sandboxTransaction: { ...prev.sandboxTransaction, blazeDataGenerationCompleted: true },
              }));
              break;
            }
          } catch {
            /* ignore, keep polling */
          }
        }
      }

      // Call backend for AI / BlazeData resolution; AI/BlazeData wins where it provides a value
      let generated: any = null;
      try {
        const response = await acApi.sandbox.generateRequest(state.sandboxTransaction.dsl);
        generated = response?.data?.result ?? null;
      } catch {
        dispatch(showToastNotification(ToastType.Danger, 'Error', 'Failed to generate values from AI/BlazeData.'));
      }

      const update = { ...literalValues };

      if (generated) {
        if (generated.path) update.path = generated.path;
        if (generated.headers) {
          update.headers = generated.headers.map((h: any) => ({
            id: generateUniqueId([]),
            name: h.name,
            value: h.value,
          }));
        }
        if (generated.queryParameters) {
          update.queryParameters = generated.queryParameters.map((p: any) => ({
            id: generateUniqueId([]),
            name: p.name,
            value: p.value,
          }));
        }
        if (generated.body) update.body = encodeBase64(generated.body);
      }

      // Persist to DB while spinner is still showing — guarantees the value is saved
      // before the user can switch transactions (mirrors the synchronous behaviour of a2fc22f5).
      try {
        await acApi.sandbox.updateSandbox({ httpRequest: update });
      } catch {
        // useEffect will retry on the next render
      }

      if (isMountedRef.current) {
        setState(prevState => ({
          ...prevState,
          sandboxTransaction: { ...prevState.sandboxTransaction, httpRequest: update },
        }));
      }
    } finally {
      setState(prev => ({ ...prev, isAutofilling: false }));
    }
  };

  useEffect(() => {
    if (!isMount) {
      updateSandbox();
    }
  }, [httpRequest]);

  const onTransfer = (keyValues: HttpKeyValue[], type: 'headers' | 'queryParams') => {
    //Transfer keyValues to the sandbox transaction
    const update = _.cloneDeep(state);
    const matchers: MatcherDsl[] = _.map(keyValues, item => {
      return { key: item.name, matcherName: Matcher.equals, matchingValue: item.value } as MatcherDsl;
    });
    if (type === 'headers') {
      const headers = [...update.sandboxTransaction.dsl.requestDsl.headers, ...matchers];
      update.sandboxTransaction.dsl.requestDsl.headers = _.uniqBy(headers, item => item.key);
    }

    if (type === 'queryParams') {
      const queryParams = [...update.sandboxTransaction.dsl.requestDsl.queryParams, ...matchers];
      update.sandboxTransaction.dsl.requestDsl.queryParams = _.uniqBy(queryParams, item => item.key);
    }

    // Mark the sandbox as dirty when query parameters are updated
    update.isSandboxDirty = true;

    setState(update);
    dispatch(
      showToastNotification(
        ToastType.Success,
        'Key-Values Transferred',
        'The key-values have been successfully transferred.',
      ),
    );
  };

  return (
    <div>
      <div>
        <Button
          variant={'outline-primary'}
          className={'float-right'}
          disabled={isAutofilling}
          title={
            'Pre-fill the request from the transaction definition, resolving BlazeData templates and AI-generated values.'
          }
          onClick={autofillValues}
        >
          {isAutofilling ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
              {'Autofilling…'}
            </>
          ) : (
            <>
              {getAccountAiConsent() && hasAiRequiredMatchers(state.sandboxTransaction.dsl) && (
                <BzmIcon variant="icon-bzm-ai-stars" className="mr-2" />
              )}
              {'Autofill Values'}
            </>
          )}
        </Button>
        <h4 className={'font-weight-semi-bold'}>Request Definition</h4>
      </div>

      <HttpRequestInput
        className={'mt-4'}
        path={httpRequest?.path}
        method={_.get(httpRequest, 'method', 'GET')}
        onUpdate={updateHandler}
        onImportHttpRequest={httpRequest => {
          setState(prevState => ({
            ...prevState,
            sandboxTransaction: {
              ...prevState.sandboxTransaction,
              httpRequest: httpRequest,
            },
          }));
        }}
        isReadOnly={false}
      />

      <Tabs className={clsx('mt-4', styles.borderedTabs)}>
        <Tab title={`Parameters (${_.size(httpRequest?.queryParameters)})`} eventKey="parameters">
          <KeyValueTable
            title={'Parameters'}
            keyValues={_.get(httpRequest, 'queryParameters', [])}
            onTransfer={keyValues => onTransfer(keyValues, 'queryParams')}
            onChange={keyValues => {
              setState(prevState => {
                const update = _.cloneDeep(prevState.sandboxTransaction.httpRequest);
                update.queryParameters = keyValues;
                return {
                  ...prevState,
                  sandboxTransaction: {
                    ...prevState.sandboxTransaction,
                    httpRequest: update,
                  },
                };
              });
            }}
          />
        </Tab>
        <Tab title={`Headers (${_.size(httpRequest?.headers)})`} eventKey="headers">
          <KeyValueTable
            title={'Headers'}
            keyValues={_.get(httpRequest, 'headers', [])}
            onTransfer={keyValues => onTransfer(keyValues, 'headers')}
            onChange={keyValues => {
              setState(prevState => {
                const update = _.cloneDeep(prevState.sandboxTransaction.httpRequest);
                update.headers = keyValues;
                return {
                  ...prevState,
                  sandboxTransaction: {
                    ...prevState.sandboxTransaction,
                    httpRequest: update,
                  },
                };
              });
            }}
          />
        </Tab>
        <Tab title={'Body'} eventKey="body">
          <RequestBody
            requestBody={httpRequest.body}
            onChange={value => {
              setState(prevState => {
                const update = _.cloneDeep(prevState.sandboxTransaction.httpRequest);
                update.body = value;
                return {
                  ...prevState,
                  sandboxTransaction: {
                    ...prevState.sandboxTransaction,
                    httpRequest: update,
                  },
                };
              });
            }}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

export default SandBoxRequestDefinitionClient;
